import React from 'react';
import { ArrowLeft, MessageCircle, Tag, ChevronRight } from 'lucide-react';
import { Service } from './types';
import { getThemeTokens } from '@/lib/themeTokens';

interface ClientDetailsViewProps {
  businessName?: string;
  portalTheme?: string;
  brandColor?: string;
  primaryService: Service;
  selectedServices: Service[];
  assignedStaffName: string;
  formattedDateString: string;
  selectedTime: string;
  phone: string;
  firstName: string;
  lastName: string;
  email: string;
  onChangeFirstName: (val: string) => void;
  onChangeLastName: (val: string) => void;
  onChangeEmail: (val: string) => void;
  onEditPhone: () => void;
  onProceed: () => void;
  onBack: () => void;
}

export const ClientDetailsView: React.FC<ClientDetailsViewProps> = ({
  businessName = 'Reservá tu turno',
  portalTheme,
  brandColor,
  primaryService,
  selectedServices,
  assignedStaffName,
  formattedDateString,
  selectedTime,
  phone,
  firstName,
  lastName,
  email,
  onChangeFirstName,
  onChangeLastName,
  onChangeEmail,
  onEditPhone,
  onProceed,
  onBack,
}) => {
  const theme = getThemeTokens(portalTheme, brandColor);
  const totalCashPrice = selectedServices.reduce((acc, s) => acc + s.cashPrice, 0);

  const cleanPhone = phone.replace(/[^0-9]/g, '');
  const displayPhone = `+54 9 ${cleanPhone}`;

  const isFormValid = firstName.trim().length >= 2 && lastName.trim().length >= 2;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid) {
      onProceed();
    }
  };

  return (
    <div style={{ color: theme.textPrimary }} className="w-full max-w-5xl mx-auto px-4 pb-24">
      {/* Barra superior con nombre de negocio y botón volver */}
      <div className="flex items-center gap-3 mb-6 pt-2">
        <button
          onClick={onBack}
          aria-label="Volver a WhatsApp"
          style={{
            backgroundColor: theme.bgSurface,
            borderColor: theme.borderSubtle,
            color: theme.textPrimary
          }}
          className="w-10 h-10 rounded-full border flex items-center justify-center hover:opacity-85 transition shadow-xs cursor-pointer shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1
          style={{ color: theme.textPrimary }}
          className="text-xl sm:text-2xl font-bold tracking-tight"
        >
          {businessName}
        </h1>
      </div>

      {/* Grid en 2 Columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Columna Izquierda: Formulario Nombre y Apellido */}
        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-8 sm:p-10 shadow-xs border flex flex-col gap-5"
          >
            <h2
              style={{ color: theme.textPrimary }}
              className="text-2xl sm:text-3xl font-bold tracking-tight text-center mb-1"
            >
              Completá tus datos
            </h2>

            {/* Fila Nombre y Apellido lado a lado */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label
                  style={{ color: theme.textSecondary }}
                  className="block text-[11px] font-bold uppercase tracking-wider mb-1"
                >
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => onChangeFirstName(e.target.value)}
                  placeholder="Ej: Ana"
                  autoFocus
                  style={{
                    backgroundColor: theme.bgSubtle,
                    borderColor: theme.borderSubtle,
                    color: theme.textPrimary
                  }}
                  className="w-full border-2 rounded-2xl px-4 py-3 font-semibold transition focus:outline-hidden focus:ring-1"
                />
              </div>

              <div>
                <label
                  style={{ color: theme.textSecondary }}
                  className="block text-[11px] font-bold uppercase tracking-wider mb-1"
                >
                  Apellido
                </label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => onChangeLastName(e.target.value)}
                  placeholder="Ej: Pérez"
                  style={{
                    backgroundColor: theme.bgSubtle,
                    borderColor: theme.borderSubtle,
                    color: theme.textPrimary
                  }}
                  className="w-full border-2 rounded-2xl px-4 py-3 font-semibold transition focus:outline-hidden focus:ring-1"
                />
              </div>
            </div>

            {/* Pastilla Verde de WhatsApp Confirmado */}
            <div
              style={{
                backgroundColor: theme.isDark ? '#06281E' : '#ECFDF5',
                borderColor: theme.isDark ? '#0F5132' : '#A7F3D0'
              }}
              className="border rounded-2xl p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-xs">
                  <MessageCircle className="w-4 h-4 fill-white text-emerald-500" />
                </div>
                <div>
                  <span
                    style={{ color: theme.isDark ? '#A1A1AA' : '#6B7280' }}
                    className="block text-[10px] font-semibold"
                  >
                    Te contactaremos al
                  </span>
                  <span
                    style={{ color: theme.isDark ? '#F4F4F5' : '#0F172A' }}
                    className="block text-xs sm:text-sm font-bold"
                  >
                    {displayPhone}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={onEditPhone}
                style={{ color: theme.isDark ? '#A1A1AA' : '#374151' }}
                className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg hover:opacity-80 transition cursor-pointer"
              >
                CAMBIAR
              </button>
            </div>

            {/* Email Opcional */}
            <div>
              <label
                style={{ color: theme.textSecondary }}
                className="block text-[11px] font-bold uppercase tracking-wider mb-1"
              >
                Email (opcional)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => onChangeEmail(e.target.value)}
                placeholder="ejemplo@email.com"
                style={{
                  backgroundColor: theme.bgSubtle,
                  borderColor: theme.borderSubtle,
                  color: theme.textPrimary
                }}
                className="w-full border-2 rounded-2xl px-4 py-3 font-semibold transition focus:outline-hidden focus:ring-1"
              />
            </div>

            {/* Botón Continuar */}
            <button
              type="submit"
              disabled={!isFormValid}
              style={{
                backgroundColor: isFormValid ? theme.brandButtonBg : theme.bgSubtle,
                color: isFormValid ? theme.brandButtonText : theme.textMuted,
                borderColor: theme.borderSubtle
              }}
              className={`w-full py-4 rounded-2xl font-bold text-sm sm:text-base mt-2 border transition duration-200 shadow-xs ${
                isFormValid ? 'hover:opacity-90 cursor-pointer' : 'cursor-not-allowed opacity-60'
              }`}
            >
              Continuar →
            </button>
          </form>
        </div>

        {/* Columna Derecha: Tarjeta Resumen */}
        <div className="lg:col-span-5">
          <div
            style={{
              backgroundColor: theme.bgSurface,
              borderColor: theme.borderSubtle
            }}
            className="rounded-3xl p-6 shadow-xs border flex flex-col gap-4"
          >
            <div className="flex flex-col gap-3">
              {selectedServices.map((srv) => (
                <div key={srv.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      style={{ backgroundColor: theme.bgSubtle }}
                      className="w-12 h-12 rounded-xl overflow-hidden shrink-0"
                    >
                      <img
                        src={srv.imageUrl || 'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=200&h=200&fit=crop'}
                        alt={srv.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3
                        style={{ color: theme.textPrimary }}
                        className="text-sm font-bold truncate"
                      >
                        {srv.name}
                      </h3>
                      <p style={{ color: theme.textSecondary }} className="text-xs">
                        👤 con {assignedStaffName}
                      </p>
                    </div>
                  </div>

                  <span
                    style={{ color: theme.textPrimary }}
                    className="font-bold text-sm whitespace-nowrap"
                  >
                    ${' '}
                    {srv.cashPrice.toLocaleString('es-AR', {
                      minimumFractionDigits: 0,
                    })}
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                backgroundColor: theme.bgSubtle,
                borderColor: theme.borderSubtle
              }}
              className="border rounded-xl p-3 flex items-center justify-between text-xs font-semibold"
            >
              <span style={{ color: theme.textMuted }}>Fecha y hora</span>
              <span style={{ color: theme.textPrimary }} className="font-bold">
                {formattedDateString} · {selectedTime} Hs
              </span>
            </div>

            <hr style={{ borderColor: theme.borderSubtle }} />

            <div className="flex items-center justify-between">
              <span style={{ color: theme.textPrimary }} className="text-sm font-bold">💵 Total</span>
              <span style={{ color: theme.textPrimary }} className="text-xl sm:text-2xl font-black">
                ${' '}
                {totalCashPrice.toLocaleString('es-AR', {
                  minimumFractionDigits: 0,
                })}
              </span>
            </div>

            <button
              style={{ color: theme.textSecondary }}
              className="text-xs hover:opacity-80 flex items-center gap-1.5 self-start cursor-pointer"
            >
              <Tag className="w-3.5 h-3.5 opacity-60" />
              <span>¿Tenés un cupón?</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
